import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { passwordResetRequest, passwordReset } from '@/api/auth';

type RequestFormData = {
  email: string;
};

type ResetFormData = {
  code: string;
  password: string;
  confirmPassword: string;
};

const requestSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

const resetSchema = z.object({
  code: z.string().min(4, 'Enter the code from your email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

/** Pull the server message out of an axios error (gateway shape: { error: string }). */
function errMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error ?? e?.message ?? fallback;
}

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';
  // If they arrived from the email link (?email=…), they already have a code → reset step.
  const [step, setStep] = useState<'request' | 'reset'>(emailParam ? 'reset' : 'request');
  const [email, setEmail] = useState(emailParam);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const requestForm = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: emailParam },
  });

  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const handleRequestSubmit = async (data: { email: string }) => {
    setServerError('');
    setSuccessMessage('');
    try {
      await passwordResetRequest(data.email);
      setEmail(data.email);
      setStep('reset');
      setSuccessMessage(`If an account exists for ${data.email}, we've emailed a 6-digit code. Enter it below to set a new password.`);
    } catch (err: unknown) {
      setServerError(errMessage(err, 'Failed to send reset email.'));
    }
  };

  const handleResetSubmit = async (data: ResetFormData) => {
    setServerError('');
    setSuccessMessage('');
    try {
      if (!email) throw new Error('Enter your email first');
      await passwordReset(email, data.code.trim(), data.password);
      setSuccessMessage('Password reset successfully! Redirecting to login…');
      setTimeout(() => (window.location.href = '/login'), 2000);
    } catch (err: unknown) {
      setServerError(errMessage(err, 'Failed to reset password.'));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-surface px-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="mb-8">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-brand-ink-soft hover:text-brand-ink mb-6">
            <ChevronLeft className="h-4 w-4" />
            Back to login
          </Link>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-brand-midnight">Reset your password</h1>
            <p className="mt-1 text-sm text-brand-ink-soft">
              {step === 'request'
                ? 'Enter your email to receive a reset code'
                : 'Enter the code we emailed you and choose a new password'}
            </p>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-6 shadow-sm">
          {/* Request Step */}
          {step === 'request' && (
            <form onSubmit={requestForm.handleSubmit(handleRequestSubmit)} className="space-y-4" noValidate>
              {serverError && (
                <div className="rounded-md bg-brand-error-soft border border-brand-error/30 px-3 py-2 text-sm text-brand-error">
                  {serverError}
                </div>
              )}

              {successMessage && (
                <div className="rounded-md bg-brand-teal-soft border border-brand-teal px-3 py-2 text-sm text-brand-teal-deep">
                  {successMessage}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...requestForm.register('email')}
                />
                {requestForm.formState.errors.email?.message && (
                  <p className="text-xs text-brand-error">{requestForm.formState.errors.email.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={requestForm.formState.isSubmitting}>
                {requestForm.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send reset code'
                )}
              </Button>
            </form>
          )}

          {/* Reset Step */}
          {step === 'reset' && (
            <form onSubmit={resetForm.handleSubmit(handleResetSubmit)} className="space-y-4" noValidate>
              {serverError && (
                <div className="rounded-md bg-brand-error-soft border border-brand-error/30 px-3 py-2 text-sm text-brand-error">
                  {serverError}
                </div>
              )}

              {successMessage && (
                <div className="rounded-md bg-brand-teal-soft border border-brand-teal px-3 py-2 text-sm text-brand-teal-deep">
                  {successMessage}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="code">Reset code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code from your email"
                  {...resetForm.register('code')}
                />
                {resetForm.formState.errors.code?.message && (
                  <p className="text-xs text-brand-error">{resetForm.formState.errors.code.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...resetForm.register('password')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ink-soft"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {resetForm.formState.errors.password?.message && (
                  <p className="text-xs text-brand-error">{resetForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    {...resetForm.register('confirmPassword')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-ink-soft"
                  >
                    {showConfirm ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {resetForm.formState.errors.confirmPassword?.message && (
                  <p className="text-xs text-brand-error">{resetForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={resetForm.formState.isSubmitting}>
                {resetForm.formState.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
